/** 429, 5xx and network failures. Safe to retry; honor retryAfterMs. */
export class TransientD365Error extends Error {
  override readonly name = 'TransientD365Error';
  constructor(
    message: string,
    readonly status: number | null,
    readonly retryAfterMs: number | null,
  ) {
    super(message);
  }
}

/** 4xx and business errors. Never retried. */
export class D365RequestError extends Error {
  override readonly name = 'D365RequestError';
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export function isTransient(err: unknown): err is TransientD365Error {
  return err instanceof TransientD365Error;
}

/** Parse a Retry-After header (seconds or HTTP date) into milliseconds. */
export function parseRetryAfter(value: string | null, nowMs: number): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isNaN(date) ? null : Math.max(0, date - nowMs);
}

const GENERIC = /^an error has occurred\.?$/i;

/**
 * Pull the message out of an OData error body without logging the body. D365 often
 * answers a failed write with "An error has occurred." and puts the reason (the Infolog
 * text) in error.innererror.message or its internalexception; prefer those. Stack traces
 * are never included.
 */
export function odataMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object' || !('error' in body)) return null;
  const messages: string[] = [];
  let node: unknown = (body as { error: unknown }).error;
  for (let depth = 0; node && typeof node === 'object' && depth < 5; depth++) {
    const m = (node as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) messages.push(m.trim());
    const n = node as { innererror?: unknown; internalexception?: unknown };
    node = n.innererror ?? n.internalexception;
  }
  const specific = messages.filter((m) => !GENERIC.test(m));
  const unique = [...new Set(specific)];
  const text = unique.length ? unique.join(' · ') : (messages[0] ?? null);
  return text && text.length > 600 ? `${text.slice(0, 600)}…` : text;
}

export function mapHttpError(
  status: number,
  body: unknown,
  retryAfterHeader: string | null,
  nowMs: number,
): TransientD365Error | D365RequestError {
  const message = odataMessage(body) ?? `D365 responded with HTTP ${status}`;
  if (status === 429 || status >= 500) {
    return new TransientD365Error(message, status, parseRetryAfter(retryAfterHeader, nowMs));
  }
  return new D365RequestError(message, status);
}
